import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import AdBanner from '../components/AdBanner';
import Breadcrumb from '../components/Breadcrumb';
import { 
  MapPin,
  Phone,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreelancerDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [freelancer, setFreelancer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPhone, setShowPhone] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [siteContent, setSiteContent] = useState({});

  // Generate multiple images for gallery
  const getGalleryImages = (images, baseImage) => {
    if (images && images.length > 0) return images;
    if (!baseImage) return [];
    return [
      baseImage,
      baseImage.replace('w=400', 'w=450').replace('h=500', 'h=550'),
      baseImage.replace('w=400', 'w=420').replace('h=500', 'h=520'),
      baseImage.replace('w=400', 'w=380').replace('h=500', 'h=480'),
      baseImage.replace('w=400', 'w=430').replace('h=500', 'h=530'),
    ];
  };

  useEffect(() => {
    fetchListing();
    axios.get(`${API}/settings/public`).then(res => setSiteContent(res.data?.content || {})).catch(() => {});
  }, [id]);

  // Track view when page loads
  useEffect(() => {
    if (id) {
      trackView();
    }
  }, [id]);

  const trackView = async () => {
    try {
      await axios.post(`${API}/analytics/track/${id}`);
    } catch (error) {
      // Silently fail - tracking is not critical
    }
  };

  const fetchListing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/listings/${id}`);
      setFreelancer(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      setFreelancer(null);
    } finally {
      setLoading(false);
    }
  };

  // Keyboard navigation for lightbox (must be before early returns)
  const imageCount = freelancer?.images?.length || 1;
  useEffect(() => {
    if (!showLightbox) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowLightbox(false);
      if (e.key === 'ArrowRight') setSelectedImageIndex(prev => (prev + 1) % imageCount);
      if (e.key === 'ArrowLeft') setSelectedImageIndex(prev => (prev - 1 + imageCount) % imageCount);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, imageCount]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (showLightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showLightbox]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 animate-fade-in-up">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="aspect-[3/4] skeleton rounded-xl" />
            <div className="space-y-4">
              <div className="h-8 w-2/3 skeleton rounded" />
              <div className="h-4 w-1/3 skeleton rounded" />
              <div className="h-4 w-full skeleton rounded mt-8" />
              <div className="h-4 w-full skeleton rounded" />
              <div className="h-4 w-3/4 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!freelancer) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-zinc-600" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('listing.profileNotFound')}</h2>
          <p className="text-zinc-400 mb-6">{t('listing.profileNotFoundDesc')}</p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button className="bg-emerald-600 hover:bg-emerald-700">{t('common.backToHome')}</Button>
            </Link>
            <Link to="/search">
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">{t('common.browseAll')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const galleryImages = getGalleryImages(freelancer.images, freelancer.images?.[0] || `https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=500&fit=crop`);

  // Get alt text for images
  const getImageAlt = (index) => {
    if (freelancer.images_data && freelancer.images_data[index]) {
      return freelancer.images_data[index].alt_text || `${freelancer.name} - ${freelancer.title}`;
    }
    return `${freelancer.name} - ${freelancer.title} image ${index + 1}`;
  };

  const handleWhatsApp = () => {
    const phone = freelancer.whatsapp.replace(/\s/g, '').replace('+', '');
    const message = encodeURIComponent(`Hi ${freelancer.name}, I saw your profile on our site and would like to work with you.`);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const selectImage = (index) => {
    setSelectedImageIndex(index);
  };

  const seoTitle = freelancer.seo?.meta_title || `${freelancer.name} - ${freelancer.title} | `;
  const seoDesc = freelancer.seo?.meta_description || `${freelancer.name}, ${freelancer.city} - ${freelancer.category}. ${(freelancer.description || '').substring(0, 150)}`;
  const seoImage = freelancer.seo?.og_image || freelancer.images?.[0] || '';

  return (
    <div className="min-h-screen bg-zinc-800 animate-fade-in-up">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={window.location.href} />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
        <link rel="canonical" href={freelancer.seo?.canonical_url || window.location.href} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": freelancer.name,
          "description": freelancer.description,
          "provider": {
            "@type": "Person",
            "name": freelancer.name,
            "address": { "@type": "PostalAddress", "addressLocality": freelancer.city }
          },
          "areaServed": freelancer.city,
          "category": freelancer.category,
          ...(seoImage && { "image": seoImage })
        })}</script>
      </Helmet>

      {/* Top Banner Ad */}
      <AdBanner position="top" />

      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: t('header.search'), href: '/search' },
          { label: freelancer.category, href: `/search?category=${encodeURIComponent(freelancer.category)}` },
          { label: freelancer.name, href: null }
        ]}
      />

      {/* Hero Section */}
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{freelancer.name}</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {freelancer.category}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Images & Content */}
          <div className="lg:col-span-2">
            {/* Main Image - Large */}
            <div className="relative mb-4">
              <div 
                className="aspect-[4/3] rounded-lg overflow-hidden bg-zinc-800 cursor-pointer relative group"
                onClick={() => setShowLightbox(true)}
              >
                <img
                  src={galleryImages[selectedImageIndex]}
                  alt={getImageAlt(selectedImageIndex)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iIzI3MjcyYSIvPjx0ZXh0IHg9IjQwMCIgeT0iMzAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNTI1MjVhIiBmb250LXNpemU9IjQ4Ij5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg=='; }}
                />
                
                {/* Navigation Arrows on Main Image */}
                <button 
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/60 px-3 py-1 rounded-full text-white text-sm">
                  {selectedImageIndex + 1} / {galleryImages.length}
                </div>
              </div>
            </div>

            {/* Thumbnail Gallery - Small Images Below */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {galleryImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => selectImage(index)}
                  className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-zinc-800 transition-all ${
                    selectedImageIndex === index 
                      ? 'ring-2 ring-emerald-500 opacity-100' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img}
                    alt={getImageAlt(index)}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>

            {/* About Section */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 mb-6">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-emerald-500 rounded-full"></span>
                {t('listing.aboutMe')}
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                {freelancer.description}
              </p>
              {siteContent.about_template_enabled !== false && (
                <p className="text-zinc-400 leading-relaxed mt-4">
                  {(siteContent.about_template || t('listing.aboutSpecialize', { category: freelancer.category.toLowerCase(), count: freelancer.review_count || 0, city: freelancer.city }))
                    .replace('{{category}}', freelancer.category?.toLowerCase() || '')
                    .replace('{{count}}', String(freelancer.review_count || 0))
                    .replace('{{city}}', freelancer.city || '')}
                </p>
              )}
            </div>

            {/* Services/Skills Section */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-emerald-500 rounded-full"></span>
                {t('listing.servicesAndSkills')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {/* Show services if available */}
                {freelancer.services && freelancer.services.length > 0 && freelancer.services.map((service, index) => (
                  <div
                    key={`service-${index}`}
                    className="flex items-center gap-2 text-emerald-400 bg-emerald-500/5 px-3 py-2 rounded-lg border border-emerald-500/10"
                  >
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
                {/* Show skills */}
                {freelancer.skills.map((skill, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-zinc-400 bg-zinc-800/50 px-3 py-2 rounded-lg"
                  >
                    <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full flex-shrink-0"></span>
                    <span className="text-sm">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Profile Info */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Profile Card */}
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 text-center relative overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-emerald-500/5 to-transparent" />

                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-zinc-800 overflow-hidden ring-2 ring-emerald-500/20 ring-offset-2 ring-offset-zinc-900">
                    <img
                      src={freelancer.images?.[0] || `https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop`}
                      alt={freelancer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h1 className="text-2xl font-bold text-white mb-1">{freelancer.name}</h1>
                  <p className="text-zinc-400 mb-3">{freelancer.title}</p>

                  <div className="inline-flex items-center gap-1.5 text-zinc-400 mb-5 px-3 py-1 rounded-full bg-zinc-800/50">
                    <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-sm">{freelancer.city}</span>
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={handleWhatsApp}
                      className="w-full bg-green-600 hover:bg-green-500 text-white py-5 transition-all hover:shadow-lg hover:shadow-green-500/20 animate-pulse-glow"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      {t('listing.sendWhatsapp')}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => setShowPhone(!showPhone)}
                      className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600 py-5 transition-all"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      {showPhone ? freelancer.phone : t('listing.callNow')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Physical Attributes Card */}
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4">{t('common.details')}</h3>
                <div className="space-y-1">
                  {freelancer.category && (
                    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800/50 group/row hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors">
                      <span className="text-zinc-500 text-sm">{t('listing.category')}</span>
                      <span className="text-white font-medium text-sm">{freelancer.category}</span>
                    </div>
                  )}
                  {freelancer.gender && (
                    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800/50 group/row hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors">
                      <span className="text-zinc-500 text-sm">{t('physicalAttributes.gender')}</span>
                      <span className="text-white font-medium text-sm capitalize">{freelancer.gender}</span>
                    </div>
                  )}
                  {freelancer.eye_color && (
                    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800/50 group/row hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors">
                      <span className="text-zinc-500 text-sm">{t('physicalAttributes.eyes')}</span>
                      <span className="text-white font-medium text-sm capitalize">{freelancer.eye_color}</span>
                    </div>
                  )}
                  {freelancer.hair_color && (
                    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800/50 group/row hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors">
                      <span className="text-zinc-500 text-sm">{t('physicalAttributes.hair')}</span>
                      <span className="text-white font-medium text-sm capitalize">{freelancer.hair_color}</span>
                    </div>
                  )}
                  {freelancer.body_type && (
                    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800/50 group/row hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors">
                      <span className="text-zinc-500 text-sm">{t('physicalAttributes.bodyType')}</span>
                      <span className="text-white font-medium text-sm capitalize">{freelancer.body_type}</span>
                    </div>
                  )}
                  {freelancer.height && (
                    <div className="flex justify-between items-center py-2.5 group/row hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors">
                      <span className="text-zinc-500 text-sm">{t('physicalAttributes.height')}</span>
                      <span className="text-white font-medium text-sm">{freelancer.height.replace('-', ' - ')} cm</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-zinc-800 py-6">
        <div className="container mx-auto px-4">
          <p className="text-zinc-600 text-sm text-center max-w-4xl mx-auto">
            {siteContent.disclaimer || t('listing.disclaimer')}
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center animate-lightbox-bg"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLightbox(false); }}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <img
            key={selectedImageIndex}
            src={galleryImages[selectedImageIndex]}
            alt={`${freelancer.name} ${selectedImageIndex + 1}`}
            className="max-h-[85vh] max-w-[85vw] object-contain animate-lightbox-img rounded-lg"
          />

          <button
            onClick={nextImage}
            className="absolute right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
            aria-label="Next image"
          >
            <ChevronRight className="w-7 h-7" />
          </button>

          {/* Image counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/10 px-4 py-1.5 rounded-full text-white text-sm font-medium">
            {selectedImageIndex + 1} / {galleryImages.length}
          </div>

          {/* Thumbnails in Lightbox */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto pb-1">
            {galleryImages.map((img, index) => (
              <button
                key={index}
                onClick={() => selectImage(index)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                  selectedImageIndex === index
                    ? 'ring-2 ring-emerald-500 opacity-100 scale-110'
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img
                  src={img}
                  alt={`Thumb ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FreelancerDetailPage;
